
import { Brain, Mic, Users, Calendar, Award, BarChart3 } from 'lucide-react';

const FeaturesSectionV3 = () => {
  const features = [
    {
      icon: Brain,
      title: 'Your AI Buddy That Never Forgets',
      headline: 'Remembers every PACT, learns your patterns',
      description: 'Show Up! tracks every commitment you make, every day you show up, and every time you don\'t. Uses this memory to understand what motivates you and suggests PACTs that actually work for your life.',
      testimonial: '"It actually remembers what worked for me last month!" - Alex K.',
      gradient: 'from-blue-500/20 to-purple-500/20',
      border: 'border-blue-400/30'
    },
    {
      icon: Mic,
      title: 'Just Tell Me What You\'re Struggling With',
      headline: 'Voice-powered PACT creation in seconds',
      description: 'Feeling unmotivated at work? Struggling with morning routine? Just speak to Show Up! and get personalized PACT suggestions instantly. No typing, no thinking - just talk.',
      testimonial: '"I just say what\'s bothering me and boom - perfect PACT!" - Maria S.',
      gradient: 'from-green-500/20 to-blue-500/20',
      border: 'border-green-400/30'
    },
    {
      icon: Users,
      title: 'Your Daily Buddy Who Actually Gets It',
      headline: 'Show up every day (even when it\'s hard)',
      description: 'Show Up! is there every single day with gentle encouragement. Not pushy reminders - just a friend saying "Hey, you\'ve got this. Show up for yourself today."',
      testimonial: '"Like having a supportive friend in my pocket." - David L.',
      gradient: 'from-yellow-500/20 to-orange-500/20',
      border: 'border-yellow-400/30'
    },
    {
      icon: BarChart3,
      title: 'Know Yourself Better Than Ever',
      headline: 'Detailed insights that actually help',
      description: 'Daily, weekly, monthly insights about your patterns. Discover when you\'re most likely to show up, what motivates you most, and how to design PACTs that stick.',
      testimonial: '"Finally understand why I succeed and fail." - Jennifer R.',
      gradient: 'from-purple-500/20 to-pink-500/20',
      border: 'border-purple-400/30'
    },
    {
      icon: Award,
      title: 'Celebrate Every Single Show Up',
      headline: 'Every day you show up is a victory',
      description: 'No shame for missed days, just celebration for showing up. Beautiful animations, encouraging messages, and genuine recognition for your effort.',
      testimonial: '"The celebrations make me feel so proud!" - Carlos M.',
      gradient: 'from-orange-500/20 to-red-500/20',
      border: 'border-orange-400/30'
    },
    {
      icon: Calendar,
      title: 'Community Challenges (Coming Soon)',
      headline: 'Show up together with people you care about',
      description: 'Create PACTs with friends and family. "Exercise for 30 days with Alex" - shared progress, mutual motivation, collective celebration when you both show up.',
      testimonial: '"Can\'t wait to do PACTs with my sister!" - Emma T.',
      gradient: 'from-pink-500/20 to-purple-500/20',
      border: 'border-pink-400/30'
    }
  ];

  return (
    <section className="container max-w-7xl mx-auto px-4 py-24">
      <div className="text-center mb-16">
        <h2 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tight">
          Your daily buddy for
          <span className="block text-yellow-400">showing up every day</span>
        </h2>
        <p className="text-xl text-white/90 max-w-3xl mx-auto font-medium leading-relaxed">
          Tired of apps that make you feel like a failure? Show Up! is different. 
          We're here to support you, understand you, and celebrate with you - every single day.
        </p>
      </div>
      
      <div className="grid lg:grid-cols-2 gap-8">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <div key={index} className={`bg-gradient-to-br ${feature.gradient} backdrop-blur-sm rounded-3xl p-8 border ${feature.border} group hover:scale-105 transition-all duration-300 hover:shadow-2xl`}>
              {/* Icon and Title */}
              <div className="flex items-start gap-6 mb-6">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-all duration-300">
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-black text-white mb-2">{feature.title}</h3>
                  <p className="text-lg font-bold text-yellow-400 mb-3">{feature.headline}</p>
                </div>
              </div>

              {/* Description */}
              <p className="text-white/90 leading-relaxed font-medium mb-6 text-lg">{feature.description}</p>

              {/* Testimonial */}
              <div className="bg-white/10 rounded-2xl p-4 border border-white/20">
                <p className="text-white/80 font-medium italic text-sm">{feature.testimonial}</p>
              </div>

              {/* App Mockup Preview (placeholder for visual enhancement) */}
              <div className="mt-6 bg-white/5 rounded-2xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-2">
                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  </div>
                  <span className="text-xs text-white/60 font-medium">Show Up! App</span>
                </div>
                <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-xl p-4 h-20 flex items-center justify-center">
                  <span className="text-white/60 text-sm font-medium">Feature Preview</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom CTA */}
      <div className="text-center mt-16">
        <div className="bg-yellow-400/20 backdrop-blur-lg rounded-3xl p-8 border border-yellow-400/30">
          <h3 className="text-3xl font-black text-white mb-4">
            Ready to show up every day?
          </h3>
          <p className="text-xl text-white/90 mb-6 font-medium">
            Join people who've discovered that showing up (even imperfectly) is what matters most.
          </p>
          <button className="bg-yellow-400 text-purple-800 hover:bg-yellow-300 rounded-2xl px-8 py-4 font-black text-lg transition-all duration-300 hover:scale-105">
            Start Your First PACT
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSectionV3;
